import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import { db } from '../firebaseConfig';
import { CUSTOMER_COMPANY_MAP } from '../config/customerConfig';

export interface CustomerDeptOption {
  customerId: string;
  label: string;
}

interface DeptRow {
  email: string;
  displayName: string;
}

const COMPANY_MAP = CUSTOMER_COMPANY_MAP as Record<string, string>;

function rollingYearRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setFullYear(start.getFullYear() - 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

/** 한 줄에 하나씩 입력 (50개 이상 대비해 여유 행) */
const ROUTE_TABLE_MIN_ROWS = 56;

function padRouteRows(routes: string[]): string[][] {
  const cleaned = routes.map(r => String(r).trim()).filter(Boolean);
  const rows = cleaned.map(r => [r]);
  while (rows.length < ROUTE_TABLE_MIN_ROWS) {
    rows.push(['']);
  }
  return rows;
}

const listBoxStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  maxHeight: 220,
  overflow: 'auto',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  background: '#fff',
  fontSize: 14,
  padding: 4,
};

const AdminDepartmentManageTab: React.FC<{ customerDeptOptions: CustomerDeptOption[] }> = ({
  customerDeptOptions,
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [departments, setDepartments] = useState<DeptRow[]>([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [selectedDeptEmail, setSelectedDeptEmail] = useState('');

  const [allItemNames, setAllItemNames] = useState<string[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  const [allowedItems, setAllowedItems] = useState<string[]>([]);
  const [filterTypeLoaded, setFilterTypeLoaded] = useState<'all' | 'allow' | 'block' | null>(null);

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [selectedRight, setSelectedRight] = useState<string | null>(null);

  const [userLoading, setUserLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveHint, setSaveHint] = useState<string | null>(null);

  const routeHotRef = useRef<any>(null);
  const [routeTableData, setRouteTableData] = useState<string[][]>([]);

  /** 거래처 표의 운송료탭 / 보고서운송료와 동일 (showStatsTab, showStatsInReport) */
  const [showStatsTab, setShowStatsTab] = useState(false);
  const [showStatsInReport, setShowStatsInReport] = useState(false);

  const fullPool = useMemo(() => {
    const set = new Set<string>([...allItemNames, ...allowedItems]);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [allItemNames, allowedItems]);

  const leftList = useMemo(
    () => fullPool.filter(n => !allowedItems.includes(n)),
    [fullPool, allowedItems]
  );

  const rightList = useMemo(
    () => [...allowedItems].sort((a, b) => a.localeCompare(b, 'ko')),
    [allowedItems]
  );

  const selectedDept = useMemo(
    () => departments.find(d => d.email === selectedDeptEmail),
    [departments, selectedDeptEmail]
  );

  const companyName = selectedCustomerId ? COMPANY_MAP[selectedCustomerId] : '';

  const loadDepartments = useCallback(async (customerId: string) => {
    if (!customerId) {
      setDepartments([]);
      return;
    }
    setDeptLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const rows: DeptRow[] = [];
      snap.docs.forEach(d => {
        const data = d.data() as Record<string, unknown>;
        if (String(data.role || '') !== 'customer') return;
        if (String(data.customerId || '').trim() !== customerId) return;
        if (data.accountType !== 'department') return;
        rows.push({
          email: d.id,
          displayName: String(data.name || '').trim() || d.id,
        });
      });
      rows.sort((a, b) => a.email.localeCompare(b.email));
      setDepartments(rows);
    } catch {
      setDepartments([]);
    } finally {
      setDeptLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDepartments(selectedCustomerId);
    setSelectedDeptEmail('');
  }, [selectedCustomerId, loadDepartments]);

  const loadItemNames = useCallback(async () => {
    if (!companyName) {
      setAllItemNames([]);
      setItemsError(null);
      return;
    }
    setItemsLoading(true);
    setItemsError(null);
    const { start, end } = rollingYearRange();
    try {
      const res = await fetch(
        `/api/settlement-reports/pg/itemNames?company=${encodeURIComponent(companyName)}&start=${start}&end=${end}`
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.error === 'string' ? err.error : '품명 목록을 불러오지 못했습니다.');
      }
      const arr = (await res.json()) as string[];
      setAllItemNames(Array.isArray(arr) ? arr.filter(Boolean) : []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '품명 목록 오류';
      setItemsError(msg);
      setAllItemNames([]);
    } finally {
      setItemsLoading(false);
    }
  }, [companyName]);

  useEffect(() => {
    void loadItemNames();
  }, [loadItemNames]);

  const loadDeptUserFilters = useCallback(async (email: string) => {
    if (!email) {
      setFilterTypeLoaded(null);
      setAllowedItems([]);
      setRouteTableData([]);
      setShowStatsTab(false);
      setShowStatsInReport(false);
      return;
    }
    setUserLoading(true);
    setSaveHint(null);
    try {
      const snap = await getDoc(doc(db, 'users', email));
      if (!snap.exists()) {
        setFilterTypeLoaded('all');
        setAllowedItems([]);
        setRouteTableData(padRouteRows([]));
        setShowStatsTab(false);
        setShowStatsInReport(false);
        return;
      }
      const data = snap.data() as Record<string, unknown>;
      setShowStatsTab(!!data.showStatsTab);
      setShowStatsInReport(!!data.showStatsInReport);
      const t = data.itemNameFilterType;
      const type: 'all' | 'allow' | 'block' =
        t === 'allow' || t === 'block' || t === 'all' ? t : 'all';
      setFilterTypeLoaded(type);
      const list = Array.isArray(data.itemNameList)
        ? (data.itemNameList as unknown[]).map(x => String(x).trim()).filter(Boolean)
        : [];
      if (type === 'allow') {
        setAllowedItems(list);
      } else {
        setAllowedItems([]);
      }
      const routeList = Array.isArray(data.routeList)
        ? (data.routeList as unknown[]).map(x => String(x).trim()).filter(Boolean)
        : [];
      setRouteTableData(padRouteRows(routeList));
    } catch {
      setFilterTypeLoaded('all');
      setAllowedItems([]);
      setRouteTableData(padRouteRows([]));
      setShowStatsTab(false);
      setShowStatsInReport(false);
    } finally {
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDeptUserFilters(selectedDeptEmail);
    setSelectedLeft(null);
    setSelectedRight(null);
  }, [selectedDeptEmail, loadDeptUserFilters]);

  const blockMode = filterTypeLoaded === 'block';

  const moveToRight = () => {
    if (!selectedLeft || blockMode) return;
    if (!allowedItems.includes(selectedLeft)) {
      setAllowedItems(prev => [...prev, selectedLeft].sort((a, b) => a.localeCompare(b, 'ko')));
    }
    setSelectedLeft(null);
  };

  const moveToLeft = () => {
    if (!selectedRight || blockMode) return;
    setAllowedItems(prev => prev.filter(x => x !== selectedRight));
    setSelectedRight(null);
  };

  const moveAllToRight = () => {
    if (blockMode) return;
    setAllowedItems(prev => {
      const next = new Set([...prev, ...leftList]);
      return Array.from(next).sort((a, b) => a.localeCompare(b, 'ko'));
    });
    setSelectedLeft(null);
  };

  const moveAllToLeft = () => {
    if (blockMode) return;
    setAllowedItems([]);
    setSelectedRight(null);
  };

  const handleSave = async () => {
    if (!selectedDeptEmail) return;
    setSaving(true);
    setSaveHint(null);
    try {
      const hot = routeHotRef.current?.hotInstance;
      const raw = (hot?.getData?.() ?? routeTableData) as string[][];
      const routes = (raw || []).map(row => String(row?.[0] ?? '').trim()).filter(Boolean);
      const routeFilterType = routes.length > 0 ? 'allow' : 'all';
      const routeList = routes;

      const payload: Record<string, unknown> = {
        routeFilterType,
        routeList,
        showStatsTab,
        showStatsInReport,
      };

      if (!blockMode) {
        const itemNameFilterType = allowedItems.length > 0 ? 'allow' : 'all';
        const itemNameList = allowedItems.length > 0 ? allowedItems : [];
        payload.itemNameFilterType = itemNameFilterType;
        payload.itemNameList = itemNameList;
      }

      await updateDoc(doc(db, 'users', selectedDeptEmail), payload);
      if (!blockMode) {
        setFilterTypeLoaded((payload.itemNameFilterType as 'all' | 'allow') || 'all');
      }
      setSaveHint('저장했습니다. 부서 계정이 다시 열면 반영됩니다.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '저장 실패';
      setSaveHint(msg);
    } finally {
      setSaving(false);
    }
  };

  const rowBtn = (label: string, onClick: () => void, disabled?: boolean, title?: string) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 12px',
        fontSize: 15,
        fontWeight: 700,
        border: '1px solid #90caf9',
        borderRadius: 8,
        background: disabled ? '#e3f2fd' : '#1976d2',
        color: disabled ? '#90a4ae' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minWidth: 52,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        gap: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 14,
          width: '100%',
          justifyContent: 'space-between',
          rowGap: 12,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', flex: '1 1 auto', minWidth: 0 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#475569' }}>
            거래처
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              style={{ minWidth: 280, padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              <option value="">선택</option>
              {customerDeptOptions.map(o => (
                <option key={o.customerId} value={o.customerId}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#475569' }}>
            부서 계정
            <select
              value={selectedDeptEmail}
              onChange={e => setSelectedDeptEmail(e.target.value)}
              disabled={!selectedCustomerId || deptLoading}
              style={{ minWidth: 300, padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              <option value="">{deptLoading ? '불러오는 중…' : '부서 선택'}</option>
              {departments.map(d => (
                <option key={d.email} value={d.email}>
                  {d.displayName} · {d.email}
                </option>
              ))}
            </select>
          </label>
          {selectedDeptEmail ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                minWidth: 200,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: 0.02 }}>화주 화면 탭 노출</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1e293b',
                    cursor: userLoading ? 'wait' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showStatsTab}
                    onChange={e => setShowStatsTab(e.target.checked)}
                    disabled={userLoading}
                    style={{ width: 18, height: 18, cursor: userLoading ? 'wait' : 'pointer' }}
                  />
                  운송료
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1e293b',
                    cursor: userLoading ? 'wait' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showStatsInReport}
                    onChange={e => setShowStatsInReport(e.target.checked)}
                    disabled={userLoading}
                    style={{ width: 18, height: 18, cursor: userLoading ? 'wait' : 'pointer' }}
                  />
                  보고서운송료
                </label>
              </div>
            </div>
          ) : null}
        </div>
        {selectedDeptEmail ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              gap: 8,
              paddingBottom: 2,
              flex: '0 1 auto',
              marginLeft: 'auto',
            }}
          >
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || userLoading || (!blockMode && itemsLoading)}
              style={{
                padding: '10px 22px',
                fontSize: 16,
                fontWeight: 700,
                border: 'none',
                borderRadius: 8,
                background: '#2e7d32',
                color: '#fff',
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving ? 0.85 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {saving ? '저장 중…' : '품명·운송구간 설정 저장'}
            </button>
            {saveHint ? (
              <span
                style={{
                  fontSize: 13,
                  color: saveHint.startsWith('저장') ? '#2e7d32' : '#c62828',
                  maxWidth: 280,
                  textAlign: 'right',
                }}
              >
                {saveHint}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {!selectedCustomerId && (
        <div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, color: '#64748b' }}>거래처를 먼저 선택하세요.</div>
      )}

      {selectedCustomerId && !COMPANY_MAP[selectedCustomerId] && (
        <div style={{ padding: 12, background: '#fff3e0', borderRadius: 8, color: '#e65100' }}>
          customerConfig에 회사명 매핑이 없는 customerId입니다. 품명 API를 호출할 수 없습니다.
        </div>
      )}

      {blockMode && selectedDeptEmail && (
        <div style={{ padding: 12, background: '#fff8e1', borderRadius: 8, color: '#856404', fontSize: 14 }}>
          이 부서는 현재 <b>품명 숨김(block)</b> 모드입니다. 이 화면은 <b>허용·전체</b>만 다룹니다. 편집하려면 거래처 탭 스프레드시트에서
          품명모드를 <b>all</b> 또는 <b>allow</b>로 바꾼 뒤 이용하세요.
        </div>
      )}

      {selectedDeptEmail && !blockMode && (
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 280px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>품명 (후보)</div>
              <div style={listBoxStyle}>
                {itemsLoading || userLoading ? (
                  <div style={{ padding: 12, color: '#64748b' }}>불러오는 중…</div>
                ) : itemsError ? (
                  <div style={{ padding: 12, color: '#c62828' }}>{itemsError}</div>
                ) : leftList.length === 0 ? (
                  <div style={{ padding: 12, color: '#94a3b8' }}>남은 품명 없음</div>
                ) : (
                  leftList.map(name => (
                    <div
                      key={name}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedLeft(name);
                        setSelectedRight(null);
                      }}
                      onDoubleClick={() => {
                        setSelectedLeft(name);
                        setAllowedItems(prev =>
                          prev.includes(name) ? prev : [...prev, name].sort((a, b) => a.localeCompare(b, 'ko'))
                        );
                        setSelectedLeft(null);
                      }}
                      style={{
                        padding: '6px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: selectedLeft === name ? '#bbdefb' : 'transparent',
                      }}
                    >
                      {name}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 8,
                paddingTop: 22,
              }}
            >
              {rowBtn('>>>', moveToRight, !selectedLeft || blockMode, '선택한 품명을 부서에 추가')}
              {rowBtn('<<<', moveToLeft, !selectedRight || blockMode, '선택한 품명을 후보로 되돌림')}
              {rowBtn('≫≫', moveAllToRight, leftList.length === 0 || blockMode, '남은 품명 전부 부서에 추가')}
              {rowBtn('≪≪', moveAllToLeft, allowedItems.length === 0 || blockMode, '부서 품명 전부 후보로 되돌림')}
            </div>

            <div style={{ flex: '1 1 280px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1976d2' }}>
                부서에 보일 품명
                {selectedDept ? ` (${selectedDept.displayName})` : ''}
              </div>
              <div style={{ ...listBoxStyle, borderColor: '#90caf9' }}>
                {userLoading ? (
                  <div style={{ padding: 12, color: '#64748b' }}>설정 불러오는 중…</div>
                ) : rightList.length === 0 ? (
                  <div style={{ padding: 12, color: '#94a3b8' }}>
                    비어 있으면 <b>전체 품명</b>이 보입니다.
                  </div>
                ) : (
                  rightList.map(name => (
                    <div
                      key={name}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedRight(name);
                        setSelectedLeft(null);
                      }}
                      onDoubleClick={() => {
                        setSelectedRight(name);
                        setAllowedItems(prev => prev.filter(x => x !== name));
                        setSelectedRight(null);
                      }}
                      style={{
                        padding: '6px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: selectedRight === name ? '#bbdefb' : 'transparent',
                      }}
                    >
                      {name}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
      )}

      {selectedDeptEmail && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
            운송구간별 노출 <span style={{ fontWeight: 500, color: '#64748b', fontSize: 13 }}>(포함 일치)</span>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
            예: <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>삼남-대풍</code>만 넣으면{' '}
            <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>삼남-대풍(냉장)</code>,{' '}
            <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>삼남-대풍2</code> 등도 표시됩니다. 창고료·대기비 같은 단일 항목도 그대로 한 줄에 입력하세요.
          </div>
          {userLoading ? (
            <div style={{ padding: 16, color: '#64748b' }}>설정 불러오는 중…</div>
          ) : (
            <div
              className="admin-manage-route-handsontable"
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              {/* afterChange에서 setState 금지: data prop 재주입 → 무한 루프. 저장 시 getData()만 사용 */}
              <HotTable
                ref={routeHotRef}
                key={selectedDeptEmail}
                data={routeTableData}
                colHeaders={['허용 운송구간']}
                columns={[{ data: 0 }]}
                rowHeaders={true}
                height="auto"
                licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
                stretchH="all"
                manualRowResize={true}
                contextMenu={['row_above', 'row_below', 'remove_row']}
              />
            </div>
          )}
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            빈 행은 저장 시 무시됩니다. 목록이 비면 운송구간 제한 없음(전체)과 같습니다.
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDepartmentManageTab;
